/**
 * @class SoulogFeatureReveal
 * Panels slide in one after another (not sticky; corresponds to flowin's disable-sticky).
 * Each .sfr__row gets .is-in when it enters the viewport, and a CSS transition does the slide-in + fade-in.
 * The media entrance style (horizontal / from below / zoom) is decided by CSS via data-media-motion; JS doesn't touch transforms.
 * On entering the viewport, the row's video is played as well.
 *
 * Replay (data-replay, theme editor toggle, on by default):
 * Two observers instead of one:
 * · The entrance observer has a bottom rootMargin of -12% and requires ratio ≥ 0.2 — keeping the original trigger timing unchanged;
 * · The reset observer has no rootMargin and threshold 0 — .is-in is removed only when the whole row has **completely** left the viewport.
 * Reusing the entrance rootMargin in a single observer causes a bug: bottom -12% means that when "not intersecting" the element actually
 * still has 12% of the viewport height visible to the eye, and when scrolling back you'd see it fade out on its own on screen.
 * With replay off, it falls back to the original behavior: trigger once, then unobserve.
 * prefers-reduced-motion: CSS doesn't hide the elements in the first place, so they're visible directly; JS still marks is-in (no side effects).
 * Shopify compatibility: scoped to the section root, re-initializes in connectedCallback, cleans up in disconnectedCallback.
 */
if (!customElements.get("soulog-feature-reveal")) {
	class SoulogFeatureReveal extends HTMLElement {
		connectedCallback() {
			this.rows = Array.from(this.querySelectorAll(".sfr__row"));
			this.io = null;
			this.outIo = null;
			this.setAttribute("data-ready", "");
			this.setup();
		}

		setup() {
			this.teardown();

			// No IntersectionObserver support: show everything directly
			if (!("IntersectionObserver" in window)) {
				this.rows.forEach((r) => this.reveal(r));
				return;
			}

			const replay = this.dataset.replay !== "false";

			this.io = new IntersectionObserver(
				(entries) => {
					for (const e of entries) {
						if (!e.isIntersecting || e.intersectionRatio < 0.2) continue;
						this.reveal(e.target);
						if (!replay) this.io.unobserve(e.target);
					}
				},
				{ rootMargin: "0px 0px -12% 0px", threshold: 0.2 },
			);
			this.rows.forEach((r) => this.io.observe(r));

			if (!replay) return;

			this.outIo = new IntersectionObserver(
				(entries) => {
					for (const e of entries) {
						if (!e.isIntersecting) this.reset(e.target);
					}
				},
				{ threshold: 0 },
			);
			this.rows.forEach((r) => this.outIo.observe(r));
		}

		reveal(row) {
			row.classList.add("is-in");
			const video = row.querySelector("video");
			if (video) {
				const play = video.play && video.play();
				if (play && play.catch) play.catch(() => {});
			}
		}

		// Return to the initial state so it replays next time it enters the viewport. Rewind the video to the start too, so a replay doesn't pick up halfway.
		reset(row) {
			row.classList.remove("is-in");
			const video = row.querySelector("video");
			if (video && video.pause) {
				video.pause();
				try {
					video.currentTime = 0;
				} catch (err) {
					/* Seeking before loading has finished throws; ignore */
				}
			}
		}

		teardown() {
			if (this.io) {
				this.io.disconnect();
				this.io = null;
			}
			if (this.outIo) {
				this.outIo.disconnect();
				this.outIo = null;
			}
		}

		disconnectedCallback() {
			this.teardown();
		}
	}
	customElements.define("soulog-feature-reveal", SoulogFeatureReveal);
}
